import React, { Component } from 'react';

interface IPaginatorProps {
    pageSize: number;
    page: number;
    totalItem: number;
    pageChange: any;
}

interface PaginatorItems {
    type: 'spread' | 'button' | 'current';
    page?: number;
}

// Number of items before spread
const SPREAD_OFFSET = 2;
// Maximum of items between lateral buttons
const MAX_ITEMS_BETWEEN = SPREAD_OFFSET * 2 + 3;

interface IPaginatorState {
    paginatorItems: PaginatorItems[];
    totalItem: number;
    isMobile: boolean;
    page: number;
}

export class Paginator extends Component<IPaginatorProps, IPaginatorState> {
    public pageCount: number = 1;

    get prevDisabled(): boolean {
        return this.props.page === 1;
    }

    get nextDisabled(): boolean {
        return this.pageCount === this.props.page;
    }

    constructor(props: IPaginatorProps) {
        super(props);
        this.state = {
            paginatorItems: [],
            isMobile: window.innerWidth < 992,
            page: props.page,
            totalItem: 0,
        };
    }

    componentDidUpdate(): void {
        if (
            this.state.page !== this.props.page ||
            this.state.totalItem !== this.props.totalItem
        ) {
            this.setState({
                totalItem: this.props.totalItem,
                page: this.props.page,
            });
            setTimeout(() => {
                this.setState({ paginatorItems: this.buildPageItems() });
            }, 0);
        }
    }

    private onResize = () => {
        this.setState({ isMobile: window.innerWidth < 992 });
        this.setState({ paginatorItems: this.buildPageItems() });
    };

    public setPage = (page: number): void => {
        this.setState({ page });
        setTimeout(() => {
            this.setState({ paginatorItems: this.buildPageItems() });
        }, 0);
        this.props.pageChange(page);
    };

    public componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
    }

    public componentDidMount(): void {
        window.addEventListener('resize', this.onResize);
        this.setState({
            totalItem: this.props.totalItem,
            page: this.props.page,
        });
        setTimeout(() => {
            this.setState({ paginatorItems: this.buildPageItems() });
        }, 0);
    }

    private buildPageItems(): PaginatorItems[] {
        this.pageCount = Math.ceil(this.state.totalItem / this.props.pageSize);
        const items: PaginatorItems[] = [];

        const length =
            this.pageCount > MAX_ITEMS_BETWEEN
                ? this.state.isMobile
                    ? MAX_ITEMS_BETWEEN - 2
                    : MAX_ITEMS_BETWEEN
                : this.pageCount;
        const maxStart = this.pageCount - length + 1;
        const minOffset = this.state.page - SPREAD_OFFSET - 1;
        const start =
            minOffset + length > this.pageCount
                ? maxStart
                : minOffset < 1
                  ? 1
                  : minOffset;
        const end = start + length - 1;

        for (let i = start; i <= end; i++) {
            const portal: PaginatorItems =
                i === this.state.page
                    ? { type: 'current', page: i }
                    : (i === start && start > 1) ||
                        (i === end && end < this.pageCount)
                      ? { type: 'spread' }
                      : { type: 'button', page: i };

            items.push(portal);
        }
        return items;
    }

    render() {
        return (
            <ul className="paginator">
                <li>
                    <button
                        onClick={() => this.setPage(1)}
                        disabled={this.prevDisabled}
                        className="icon-button"
                    >
                        <i className="icon-doubleAngleLeft"></i>
                    </button>
                </li>
                <li>
                    <button
                        onClick={() => this.setPage(this.props.page - 1)}
                        disabled={this.prevDisabled}
                        className="icon-button"
                    >
                        <i className="icon-angleLeft"></i>
                    </button>
                </li>
                {this.state.paginatorItems.map(
                    (item: PaginatorItems, i: number) => {
                        return (
                            <li key={i}>
                                {item.type === 'spread' && (
                                    <span className="spread">...</span>
                                )}
                                {item.type === 'button' &&
                                    item.page !== undefined &&
                                    (() => {
                                        const pageNumber = item.page;
                                        return (
                                            <button
                                                className="page-button"
                                                onClick={() =>
                                                    this.setPage(pageNumber)
                                                }
                                            >
                                                {pageNumber}
                                            </button>
                                        );
                                    })()}
                                {item.type === 'current' && (
                                    <span className="current-page">
                                        {item.page}
                                    </span>
                                )}
                            </li>
                        );
                    }
                )}
                <li>
                    <button
                        onClick={() => this.setPage(this.props.page + 1)}
                        disabled={this.nextDisabled}
                        className="icon-button"
                    >
                        <i className="icon-angleRight"></i>
                    </button>
                </li>
                <li>
                    <button
                        onClick={() => this.setPage(this.pageCount)}
                        disabled={this.nextDisabled}
                        className="icon-button"
                    >
                        <i className="icon-doubleAngleRight"></i>
                    </button>
                </li>
            </ul>
        );
    }
}
